const NAMED_COLORS: Record<string, string> = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff4d4f",
  volcano: "#fa541c",
  orange: "#fa8c16",
  gold: "#faad14",
  yellow: "#fadb14",
  lime: "#a0d911",
  green: "#52c41a",
  cyan: "#13c2c2",
  blue: "#1677ff",
  geekblue: "#2f54eb",
  purple: "#722ed1",
  magenta: "#eb2f96",
  pink: "#eb2f96",
  success: "#52c41a",
  warning: "#faad14",
  error: "#ff4d4f",
  processing: "#1677ff",
  default: "#d9d9d9",
};

function hexToRgb(hex: string): [number, number, number] | null {
  const normalized = hex.replace("#", "").trim();
  if (!/^[\da-f]{3}$|^[\da-f]{6}$/i.test(normalized)) {
    return null;
  }

  const full =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : normalized;

  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

function rgbStringToRgb(value: string): [number, number, number] | null {
  const trimmed = value.trim();
  const modern = trimmed.match(
    /^rgba?\(\s*(\d{1,3})\s+(\d{1,3})\s+(\d{1,3})(?:\s*\/\s*[\d.]+%?)?\s*\)$/i,
  );
  if (modern) {
    return [
      Math.min(255, Number(modern[1])),
      Math.min(255, Number(modern[2])),
      Math.min(255, Number(modern[3])),
    ];
  }

  const legacy = trimmed.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i,
  );
  if (!legacy) {
    return null;
  }

  return [
    Math.min(255, Number(legacy[1])),
    Math.min(255, Number(legacy[2])),
    Math.min(255, Number(legacy[3])),
  ];
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const transform = (channel: number): number => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };

  const [rs, gs, bs] = [transform(r), transform(g), transform(b)];
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

export function getReadableTagTextColor(
  backgroundColor?: string | null,
): string | undefined {
  const isDarkTheme =
    typeof document !== "undefined" &&
    document.body?.dataset?.theme === "dark";
  if (!isDarkTheme) {
    return undefined;
  }

  if (!backgroundColor) {
    return undefined;
  }

  const source = backgroundColor.trim().toLowerCase();
  const resolved = NAMED_COLORS[source] ?? source;

  const rgb = resolved.startsWith("#")
    ? hexToRgb(resolved)
    : rgbStringToRgb(resolved);
  if (!rgb) {
    return undefined;
  }

  const luminance = relativeLuminance(rgb);
  return luminance > 0.52 ? "rgba(0, 0, 0, 0.88)" : "#ffffff";
}
